import apiClient from '@/api/client';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatRequest {
  message: string;
  conversation_history: ChatMessage[];
  context_type?: string;
}

export interface ChatResponse {
  reply: string;
  model: string;
  tokens_used: number;
  context_type?: string;
}

export interface BulkSummaryRequest {
  data_type: 'revenue' | 'expenses' | 'payroll' | 'receivables';
  year: number;
  month?: number;
}

export interface BulkSummaryResponse {
  summary: string;
  model: string;
  tokens_used: number;
  data_type: string;
  period: string;
}

export interface ChatSource {
  id?: string;
  label?: string;
  kind?: string;
  [key: string]: unknown;
}

export type ChatStreamEvent =
  | { type: 'start'; model: string }
  | { type: 'text'; delta: string }
  | {
      type: 'done';
      model: string;
      input_tokens: number;
      output_tokens: number;
      stop_reason: string;
      sources: ChatSource[];
    }
  | { type: 'error'; error: string; message: string };

const API_BASE = (import.meta.env.VITE_API_URL || '') + '/api/v1';

/**
 * Streaming chat — yields protocol events. Uses fetch + ReadableStream
 * so we inherit the httpOnly auth cookie from the main axios config
 * (EventSource would, but doesn't support POST).
 *
 * If the backend returns a non-200 (e.g. 402 quota exhausted), we throw
 * an Error carrying the parsed detail so the caller can show the upgrade
 * prompt instead of silently hanging on a non-event-stream body.
 */
export async function* streamChat(
  data: ChatRequest,
  signal?: AbortSignal,
): AsyncGenerator<ChatStreamEvent, void, void> {
  const res = await fetch(`${API_BASE}/ai/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    credentials: 'include',
    body: JSON.stringify(data),
    signal,
  });

  if (!res.ok || !res.body) {
    let detail: string | undefined;
    try {
      const body = await res.json();
      detail = body?.detail;
    } catch {
      // fall through to generic message
    }
    const err = new Error(detail || `Chat stream failed (${res.status})`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by blank lines. Keep the trailing
      // partial frame in the buffer for the next read.
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
        if (!dataLine) continue;
        const payload = dataLine.replace(/^data:\s?/, '').trim();
        if (!payload) continue;
        try {
          yield JSON.parse(payload) as ChatStreamEvent;
        } catch {
          // Ignore unparseable frames — never abort the stream for a single bad frame
        }
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // lock may already be released if the reader cancelled
    }
  }
}

// ── Copilot streaming types ──

export interface CopilotRequest {
  query: string;
  conversation_history: { role: 'user' | 'assistant'; content: string }[];
  year?: number;
}

export type CopilotStreamEvent =
  | { type: 'start'; model: string }
  | { type: 'thinking' }
  | { type: 'tool_call'; tool: string; label: string }
  | { type: 'tool_result'; tool: string; rows: number; ok: boolean }
  | { type: 'tool_error'; tool: string; error: string }
  | { type: 'text'; delta: string }
  | {
      type: 'done';
      tools_called: string[];
      input_tokens: number;
      output_tokens: number;
      duration_ms: number;
    }
  | { type: 'error'; error: string; message: string };

/**
 * Streaming Copilot — yields SSE events showing tool activity + text.
 * Same SSE framing as streamChat; uses the analytics copilot endpoint.
 */
export async function* streamCopilot(
  data: CopilotRequest,
  signal?: AbortSignal,
): AsyncGenerator<CopilotStreamEvent, void, void> {
  const res = await fetch(`${API_BASE}/ai/analytics/copilot/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    credentials: 'include',
    body: JSON.stringify(data),
    signal,
  });

  if (!res.ok || !res.body) {
    let detail: string | undefined;
    try {
      const body = await res.json();
      detail = body?.detail;
    } catch {
      // fall through to generic message
    }
    const err = new Error(detail || `Copilot stream failed (${res.status})`);
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
        if (!dataLine) continue;
        const payload = dataLine.replace(/^data:\s?/, '').trim();
        if (!payload) continue;
        try {
          yield JSON.parse(payload) as CopilotStreamEvent;
        } catch {
          // Ignore unparseable frames
        }
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // lock may already be released
    }
  }
}

export const aiApi = {
  chat: (data: ChatRequest) =>
    apiClient.post<ChatResponse>('/ai/chat', data),
  summary: (data: BulkSummaryRequest) =>
    apiClient.post<BulkSummaryResponse>('/ai/summary', data),
  streamChat,
  streamCopilot,
};
